import React from 'react'
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { Toaster } from 'react-hot-toast'
import { AuthProvider, useAuth } from './context/AuthContext'
import { LoadingProvider } from './context/LoadingContext'
import { modernTheme } from './theme/modernTheme'
import GlobalLoader from './components/GlobalLoader'

// Pages
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Users from './pages/Users'
import UserFormPage from './pages/UserFormPage'
import Profile from './pages/Profile'
import Contacts from './pages/Contacts'
import BulkUploadPage from './pages/BulkUploadPage'
import ContactDetailPage from './pages/ContactDetailPage'
import Donations from './pages/Donations'
import DonationFormPage from './pages/DonationFormPage'
import Campaigns from './pages/Campaigns'
import CampaignDetailPage from './pages/CampaignDetailPage'
import Receipts from './pages/Receipts'
import EmailTemplates from './pages/EmailTemplates'
import EmailTemplateEditor from './pages/EmailTemplateEditor'
import Tags from './pages/Tags'
import DonationSchemas from './pages/DonationSchemas'
import Reminders from './pages/Reminders'
import ReminderDetailPage from './pages/ReminderDetailPage'
import Layout from './components/Layout'
import WhatsAppTemplates from "./pages/WhatsAppTemplates";
import WhatsAppTemplateDetailPage from "./pages/WhatsAppTemplateDetailPage";
import WhatsAppTemplateFormPage from "./pages/WhatsAppTemplateFormPage";
import { useDispatch, useSelector } from 'react-redux'
import { fetchTags, selectTagsStatus } from './store/slices/tagsSlice'

// Modern theme is imported from theme/modernTheme.js

import LoadingPage from './components/LoadingPage'

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return <LoadingPage />
  }

  return isAuthenticated ? children : <Navigate to="/login" />
}

function AppRoutes() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }>
          <Route index element={<Dashboard />} />
          <Route path="users" element={<Users />} />
          <Route path="users/new" element={<UserFormPage />} />
          <Route path="users/:id" element={<UserFormPage />} />
          <Route path="profile" element={<Profile />} />
          <Route path="contacts" element={<Contacts />} />
          <Route path="contacts/import" element={<BulkUploadPage />} />
          <Route path="contacts/:id" element={<ContactDetailPage />} />
          <Route path="donations" element={<Donations />} />
          <Route path="donations/new" element={<DonationFormPage />} />
          <Route path="donations/:id" element={<DonationFormPage />} />
          <Route path="campaigns" element={<Campaigns />} />
          <Route path="campaigns/:id" element={<CampaignDetailPage />} />
          <Route path="receipts" element={<Receipts />} />
          <Route path="email-templates" element={<EmailTemplates />} />
          <Route path="email-templates/new" element={<EmailTemplateEditor />} />
          <Route path="email-templates/:id" element={<EmailTemplateEditor />} />
          <Route path="whatsapp-templates" element={<WhatsAppTemplates />} />
          <Route path="whatsapp-templates/create" element={<WhatsAppTemplateFormPage />} />
          <Route path="whatsapp-templates/edit/:id" element={<WhatsAppTemplateFormPage />} />
          <Route
            path="whatsapp-templates/:id"
            element={<WhatsAppTemplateDetailPage />}
          />
          <Route path="tags" element={<Tags />} />
          <Route path="donation-schemas" element={<DonationSchemas />} />
          <Route path="reminders" element={<Reminders />} />
          <Route path="reminders/:id" element={<ReminderDetailPage />} />
        </Route>
      </Routes>
    </Router>
  )
}

function App() {
  const dispatch = useDispatch()
  const tagsStatus = useSelector(selectTagsStatus)

  React.useEffect(() => {
    if (tagsStatus === 'idle') {
      dispatch(fetchTags({ limit: 1000 }))
    }
  }, [tagsStatus, dispatch])

  return (
    <ThemeProvider theme={modernTheme}>
      <CssBaseline />
      <LoadingProvider>
        <GlobalLoader />
        <Toaster
          position="top-center"
          reverseOrder={false}
          gutter={8}
          toastOptions={{
            duration: 3000,
            style: {
              borderRadius: '8px',
              background: '#FFFFFF',
              color: '#000000',
              fontSize: '14px',
              fontWeight: '500',
              padding: '14px 20px',
              boxShadow: '0 4px 12px rgba(90, 106, 207, 0.15)',
              border: '1px solid #E5E7EB',
              fontFamily: '"Rajdhani", sans-serif',
              minWidth: '300px',
            },
          }}
        />
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </LoadingProvider>
    </ThemeProvider>
  )
}

export default App
